'use client';

import { customerFetch } from '@/lib/customer-bridge';
import { clearGuestIdentity } from '@/lib/customer/guest-identity';
import { listGuestOrders } from '@/lib/customer/guest-orders';
import { listGuestReservations } from '@/lib/customer/guest-reservations';

// Signup hand-off (doc 52): everything this device stored while browsing as a
// guest is claimed onto the account the moment the guest signs up, so nothing
// is lost and the temporary guest handle can go away.
//
// Claims use the bearer credentials, never the username: order access tokens
// for orders (POST /api/customer-auth/orders/claim) and the request id for
// reservation requests (POST /api/customer-auth/reservations/claim). Both are
// idempotent, so running them on every sign-in is safe.

export type GuestClaimResult = {
  ordersClaimed: number;
  reservationsClaimed: number;
  failures: number;
};

export async function claimGuestDeviceData(): Promise<GuestClaimResult> {
  const result: GuestClaimResult = { ordersClaimed: 0, reservationsClaimed: 0, failures: 0 };

  if (typeof window === 'undefined') return result;

  const orders = listGuestOrders();
  for (const record of orders) {
    try {
      const res = await customerFetch('/api/customer-auth/orders/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken: record.token }),
      });
      if (res.ok) {
        result.ordersClaimed += 1;
      } else {
        result.failures += 1;
      }
    } catch {
      result.failures += 1;
    }
  }

  const reservations = listGuestReservations();
  for (const record of reservations) {
    try {
      const res = await customerFetch('/api/customer-auth/reservations/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestId: record.id }),
      });
      if (res.ok) {
        result.reservationsClaimed += 1;
      } else {
        result.failures += 1;
      }
    } catch {
      result.failures += 1;
    }
  }

  // Best-effort regardless of claim success: the guest handle is a label, and
  // the account username now replaces it.
  clearGuestIdentity();

  return result;
}
